import { isNil } from 'lodash';
import * as moment from 'moment';
import { DATE_TIME_FORMAT } from './formatters';
import { LinkParams } from '../covance-ag-grid';
import { Distance } from '../shared';

let stringCollator: Intl.Collator;
let numericStringCollator: Intl.Collator;

if (window.hasOwnProperty('Intl') && Intl.Collator) {
    stringCollator = new Intl.Collator(undefined, { numeric: false, sensitivity: 'base' });
    numericStringCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    console.debug('Collator API is available');
}

export const comparators = {
    stringComparator: getStringComparator(numericStringCollator),
    mixedStringComparator: getStringComparator(stringCollator),

    dateTimeComparator(valueA: string | moment.Moment, valueB: string | moment.Moment) {

        const missingValuesComparison = missingValuesComparer(valueA, valueB);
        if (missingValuesComparison !== null) {
            return missingValuesComparison;
        }

        const momentA = (typeof valueA === 'string') ? moment(valueA, DATE_TIME_FORMAT) : valueA;
        const momentB = (typeof valueB === 'string') ? moment(valueB, DATE_TIME_FORMAT) : valueB;

        const isMomentAValid = momentA && momentA.isValid();
        const isMomentBValid = momentB && momentB.isValid();
        if (isMomentAValid && isMomentBValid) {
            if (momentA.isAfter(momentB)) {
                return 1;
            }
            if (momentA.isBefore(momentB)) {
                return -1;
            }
            return 0;
        }
        if (isMomentAValid && !isMomentBValid) {
            return 1;
        }
        if (!isMomentAValid && isMomentBValid) {
            return -1;
        }
        return 0;
    },

    numberComparator(valueA: number, valueB: number) {
        const missingValuesComparison = missingValuesComparer(valueA, valueB);
        if (missingValuesComparison !== null) {
            return missingValuesComparison;
        }

        return valueA - valueB;
    },

    linkParamsComparator(a: LinkParams, b: LinkParams) {
        const valueA = a ? a.text : null;
        const valueB = b ? b.text : null;
        return comparators.mixedStringComparator(valueA, valueB);
    },

    linkNumberParamsComparator(a: LinkParams, b: LinkParams) {
        const valueA = a ? a.text : null;
        const valueB = b ? b.text : null;
        return comparators.numberComparator(+valueA, +valueB);
    },

    distanceComparator(a: Distance, b: Distance) {
        const valueA = a ? a.ticks : null;
        const valueB = b ? b.ticks : null;
        return comparators.numberComparator(valueA, valueB);
    }
};

///returns number if one or two values are missing and null if not.
function missingValuesComparer(valueA: any, valueB: any) {
    const valueAMissing = isNil(valueA);
    const valueBMissing = isNil(valueB);

    if (valueAMissing && valueBMissing) {
        return 0;
    }
    if (valueAMissing) {
        return -1;
    }
    if (valueBMissing) {
        return 1;
    }

    return null;
}

function getStringComparator(collator: Intl.Collator) {
    return (valueA: string | number, valueB: string | number): number => {
        const missingValuesComparison = missingValuesComparer(valueA, valueB);
        if (missingValuesComparison !== null) {
            return missingValuesComparison;
        }

        const stringValueA = valueA.toString();
        const stringValueB = valueB.toString();

        return collator
            ? collator.compare(stringValueA, stringValueB)
            : stringValueA.localeCompare(stringValueB, undefined, { numeric: collator.resolvedOptions().numeric });
    };
}
